/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: (this.isCartJourney || document.querySelector('.zs-backpack-cross-sell, .zs-category-cross-sell'))
            ? fetch(evt.target.dataset.productUrl).catch(() => null)
            : fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.returnToCartDrawer = false;
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      this.isCartJourney = Boolean(evt.target.closest('.zs-cart-journey'));
      this.returnToCartDrawer = this.isCartJourney && Boolean(evt.target.closest('#cart-drawer'));
      this.cartModel = this.isCartJourney ? evt.target.dataset.zsCartModel || '' : '';
      this.cartRemaining = this.isCartJourney ? Number(evt.target.closest('.zs-cart-journey').dataset.remaining || 0) : 0;
      this.toggleAttribute('data-zs-cart-context', this.isCartJourney);
      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      if ((this.isCartJourney || document.querySelector('.zs-backpack-cross-sell, .zs-category-cross-sell'))) return this.openOrganizerDraft(opener);
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector('.cc-main-product .js-product');
        this.renderProduct(opener);
      }

      this.fetch = null;

      opener.removeAttribute('aria-disabled');
    }


    /**
     * Draft quick-add: bounded loading, small source cache, retry UI and stale-request protection.
     */
    async openOrganizerDraft(opener) {
      const url = opener.dataset.productUrl;
      opener.setAttribute('aria-disabled', 'true');
      if (this.notification) this.notification.hidden = true;
      if (this.productUrl === url && this.draftProductReady && !this.isCartJourney) {
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.draftAbort?.abort();
      const requestId = (this.draftLoadId || 0) + 1;
      this.draftLoadId = requestId;
      this.draftAbort = new AbortController();
      const controller = this.draftAbort;
      this.productUrl = url;
      this.draftProductReady = false;
      this.content.innerHTML = '<p class="zs-quick-add-loading" role="status">Produkt wird geladen…</p>';
      this.content.classList.remove('drawer__content--out');
      this.classList.add('is-loading');
      this.footer.hidden = true;
      super.open(opener);
      let timeout;
      try {
        this.draftProductCache = this.draftProductCache || new Map();
        let source = this.draftProductCache.get(url);
        if (!source) {
          const download = async () => {
            let response = this.fetch?.url === url ? await this.fetch.promise : null;
            if (!response) response = await fetch(url, { signal: controller.signal });
            if (!response.ok) throw new Error('Product request failed');
            return response.text();
          };
          source = await Promise.race([
            download(),
            new Promise((_, reject) => {
              timeout = setTimeout(() => {
                controller.abort();
                reject(new Error('Product request timed out'));
              }, 12000);
            })
          ]);
        }
        if (requestId !== this.draftLoadId || !this.hasAttribute('open')) return;
        const template = document.createElement('template');
        template.innerHTML = source;
        const product = template.content.querySelector('.cc-main-product .js-product');
        if (!product) throw new Error('Product form missing');
        // Cache just the product section, not the entire storefront document.
        this.draftProductCache.set(url, '<div class="cc-main-product">' + product.outerHTML + '</div>');
        while (this.draftProductCache.size > 4) {
          this.draftProductCache.delete(this.draftProductCache.keys().next().value);
        }
        this.productEl = product;
        this.renderProduct(opener);
        this.draftProductReady = true;
      } catch (error) {
        if (requestId !== this.draftLoadId || !this.hasAttribute('open')) return;
        this.content.innerHTML = '';
        const message = document.createElement('p');
        message.className = 'zs-quick-add-load-error';
        message.setAttribute('role', 'alert');
        message.textContent = 'Das Produkt konnte nicht geladen werden. Bitte versuche es erneut.';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'zs-quick-add-retry btn btn--primary';
        retry.textContent = 'Erneut versuchen';
        retry.addEventListener('click', () => this.open(opener));
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'zs-quick-add-done js-close-drawer';
        close.textContent = this.isCartJourney ? 'Zurück zum Warenkorb' : document.querySelector('.zs-bundle-intro') ? 'Zurück zum Set' : document.querySelector('.zs-backpack-cross-sell') ? 'Zurück zum Rucksack' : 'Zurück zum Produkt';
        this.content.append(message, retry, close);
      } finally {
        clearTimeout(timeout);
        opener.removeAttribute('aria-disabled');
        if (requestId === this.draftLoadId) {
          this.classList.remove('is-loading');
          this.content.classList.remove('drawer__content--out');
          this.fetch = null;
        }
      }
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      if ((this.isCartJourney || document.querySelector('.zs-backpack-cross-sell, .zs-category-cross-sell'))) {
        this.draftLoadId = (this.draftLoadId || 0) + 1;
        this.draftAbort?.abort();
        this.classList.remove('is-loading');
      }
      const returnToCart = this.returnToCartDrawer;
      this.returnToCartDrawer = false;
      super.close(() => {
        this.backBtn.hidden = true;
        if (returnToCart) this.cartDrawer?.open();
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
        this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = this.productEl.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }

      this.updateContent();
      this.updateForm();

      if ((this.isCartJourney || document.querySelector('.zs-backpack-cross-sell, .zs-category-cross-sell'))) {
        setTimeout(() => this.selectOrganizerDraftDefault(), 0);
      }

      // Update the product media.
      const activeMedia = this.productEl.querySelector('.media-viewer__item.is-current-variant');
      if (activeMedia) this.updateMedia(activeMedia.dataset.mediaId);

      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }


    /**
     * Preselect an available variant in the Organizer 30L draft quick-add flow.
     * Accessory model groups containing Organizer 30L take priority over other backpacks.
     */
    selectOrganizerDraftDefault() {
      const picker = this.querySelector('variant-picker');
      if (!picker || !picker.data?.product?.variants) return;
      const available = picker.data.product.variants.filter((variant) => variant.available);
      const context = this.isCartJourney ? null : document.querySelector('.zs-backpack-cross-sell, .zs-category-cross-sell');
      const selectedModel = this.isCartJourney ? this.cartModel : context?.dataset.zsSelectedModel || '';
      const family = context?.dataset.zsBackpackFamily || selectedModel.match(/Organizer|Underseat|Cabin|Rolltop|Weekender/i)?.[0] || '';
      const sizes = context?.dataset.zsBackpackSize ? [context.dataset.zsBackpackSize] : (selectedModel.match(/\d+L/g) || []);
      const modelVariants = picker.data.product.variants.filter((variant) =>
        variant.options.some((option) => family && option.toLowerCase().includes(family.toLowerCase())
          && (family.toLowerCase() === 'rolltop' || !sizes.length || sizes.some((size) => option.replace(/(\d+)(?![\dL])/g, '$1L').split(/[\s/]+/).includes(size))))
      );
      const target = modelVariants.length
        ? (modelVariants.find((variant) => variant.available) || modelVariants[0])
        : available[0];
      if (!target) return;

      picker.optionSelectors.forEach((selector, index) => {
        const value = target.options[index];
        if (selector.dataset.selectorType === 'dropdown') {
          const option = Array.from(selector.querySelectorAll('.custom-select__option'))
            .find((element) => element.dataset.value === value);
          const select = selector.querySelector('custom-select');
          if (option && select?.selectOption) select.selectOption(option);
        } else {
          const radio = Array.from(selector.querySelectorAll('input[type="radio"]'))
            .find((input) => input.value === value);
          if (radio && !radio.checked) radio.click();
        }
      });
      picker.selectedOptions = picker.getSelectedOptions();
      picker.variant = picker.getSelectedVariant();
      picker.preSelection = false;
      if (picker.variant) picker.updateVariantInput();
      picker.updateAddToCartButton();
      picker.updateAvailability();
      picker.updatePrice();
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId) {
      const img = this.productEl.querySelector(`[data-media-id="${mediaId}"] img`);
      if (!img) return;

      const src = img.src ? img.src.split('&width=')[0] : img.dataset.src.split('&width=')[0];
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = img.width / img.height;

      container.innerHTML = `
        <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${img.alt}">
      `;
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      let weightElem = this.getElementHtml('.product-info__weight');
      if (weightElem && weightElem.length > 0) {
        weightElem = `<div class="product-info__weight text-sm mt-2">${weightElem}</div>`;
      }

      this.content.innerHTML = `
        <div class="quick-add-info grid mb-8">
          <div class="quick-add-info__media${theme.settings.blendProductImages ? ' image-blend' : ''}"></div>
          <div class="quick-add-info__details">
            <div class="product-vendor-sku mb-2 text-sm">
              ${this.getElementHtml('.product-vendor-sku')}
            </div>
            <div class="product-title">
              <a class="h6 js-prod-link" href="${this.productUrl}">
                ${this.getElementHtml('.product-title')}
              </a>
            </div>
            ${weightElem}
            <hr>
            <div class="product-price">
              ${this.getElementHtml('.product-price')}
            </div>
            <div class="text-theme-light text-sm mt-4">
              <a href="${this.productUrl}" class="link js-prod-link">
                ${theme.strings.viewDetails}
              </a>
            </div>
          </div>
          <div class="quick-add-info__details md:hidden"></div>
        </div>
        <div class="product-options">
          ${this.getElementHtml('.product-options', '.custom-option:not(.quick-add-hidden)')}
        </div>
        <div class="product-backorder">
          ${this.getElementHtml('.product-backorder')}
        </div>
      `;

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      if (!(this.isCartJourney || document.querySelector('.zs-backpack-cross-sell, .zs-category-cross-sell'))) {
        this.footer.appendChild(this.form);
        if (this.notification) this.footer.appendChild(this.notification);
        this.footer.hidden = false;
        this.footer.classList.remove('zs-quick-add-footer-empty');
      }
      this.footer.classList.remove('quick-add__footer-message');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        const signUpForm = this.productEl.querySelector('.product-signup');
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add('quick-add__footer-message');
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove('drawer__footer--out');

      // Organizer draft: keep the actual native form after the choices in the scrollable panel.
      // Reattach the same form on every render; updateContent may detach it between products.
      if ((this.isCartJourney || document.querySelector('.zs-backpack-cross-sell, .zs-category-cross-sell')) && productForm) {
        const action = document.createElement('div');
        action.className = 'zs-quick-add-action';
        const purchaseRow = this.form.querySelector('.product-info__add-to-cart');
        const quantity = this.form.querySelector('[name="quantity"]');
        if (purchaseRow) purchaseRow.classList.add('zs-quick-add-purchase-row');
        if (purchaseRow && quantity && !this.form.querySelector('quantity-input')) {
          const controls = document.createElement('div');
          controls.className = 'zs-quick-add-qty';
          controls.setAttribute('role', 'group');
          controls.setAttribute('aria-label', 'Anzahl');
          const minus = document.createElement('button');
          const plus = document.createElement('button');
          minus.type = plus.type = 'button';
          minus.textContent = '−';
          plus.textContent = '+';
          minus.setAttribute('aria-label', 'Weniger');
          plus.setAttribute('aria-label', 'Mehr');
          quantity.type = 'number';
          quantity.min = quantity.min || '1';
          quantity.step = quantity.step || '1';
          quantity.value = '1';
          quantity.className = 'zs-quick-add-qty__input';
          quantity.setAttribute('aria-label', 'Anzahl');
          quantity.replaceWith(controls);
          controls.append(minus, quantity, plus);
          purchaseRow.prepend(controls);
          const updateMinimum = () => {
            if (!quantity.value || Number(quantity.value) < Number(quantity.min)) quantity.value = quantity.min;
            minus.disabled = Number(quantity.value) <= Number(quantity.min);
          };
          minus.addEventListener('click', () => {
            quantity.stepDown();
            quantity.dispatchEvent(new Event('change', { bubbles: true }));
          });
          plus.addEventListener('click', () => {
            quantity.stepUp();
            quantity.dispatchEvent(new Event('change', { bubbles: true }));
          });
          quantity.addEventListener('change', updateMinimum);
          updateMinimum();
        }
        if (window.Shopify?.theme?.role === 'unpublished') {
          this.style.setProperty('--zs-preview-offset', '60px');
        }
        action.appendChild(this.form);
        if (this.notification) action.appendChild(this.notification);
        const done = document.createElement('button');
        done.type = 'button';
        done.className = 'zs-quick-add-done js-close-drawer';
        done.textContent = this.isCartJourney ? 'Zurück zum Warenkorb' : document.querySelector('.zs-bundle-intro') ? 'Zurück zum Set' : document.querySelector('.zs-backpack-cross-sell') ? 'Zurück zum Rucksack' : 'Zurück zum Produkt';
        if (!this.isCartJourney) action.appendChild(done);
        this.content.appendChild(action);
        this.footer.classList.add('zs-quick-add-footer-empty');
      }
    }

    /**
     * Gets the innerHTML of elements within the product element.
     * @param {string} selector - CSS selector for the element.
     * @param {...*} additionalSelectors - Additional CSS selectors for elements.
     * @returns {?string}
     */
    getElementHtml(selector, ...additionalSelectors) {
      const selectors = [selector].concat(additionalSelectors);
      let html = '';
      selectors.forEach((sel) => {
        const els = this.productEl.querySelectorAll(sel);
        Array.from(els).forEach((el) => {
          html += el.innerHTML;
        });
      });
      return html;
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.isCartJourney) {
        this.close();
        return;
      }
      if ((this.isCartJourney || document.querySelector('.zs-backpack-cross-sell, .zs-category-cross-sell'))) {
        if (this.notification) {
          const heading = this.notification.querySelector('.quick-add__alert-heading');
          if (heading) heading.textContent = 'Zum Warenkorb hinzugefügt';
          this.notification.hidden = false;
        }
        const done = this.content.querySelector('.zs-quick-add-done');
        if (done) requestAnimationFrame(() => done.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
        return;
      }
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}
